import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Plus, Trash2, GripVertical, Star, Loader2 } from 'lucide-react';
import { API, adminAuthHeader } from '../api';
import ImageUploader from './ImageUploader';

const SiteContentManager = () => {
  const [tab, setTab] = useState('general');
  return (
    <div>
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Site Content</h2>
          <p className="text-sm text-gray-500">Edit sections shown across your website — hero, footer, partners and testimonials.</p>
        </div>
        <div className="inline-flex bg-white border border-gray-200 rounded-md p-1 flex-wrap">
          {[
            { k: 'general', label: 'General' },
            { k: 'branding', label: 'Branding & Socials' },
            { k: 'search', label: 'Search Bar' },
            { k: 'benefits', label: 'Benefits' },
            { k: 'partners', label: 'Partners' },
            { k: 'testimonials', label: 'Testimonials' },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`site-tab-${t.k}`}
              className={`px-3 py-1.5 text-sm rounded ${tab === t.k ? 'bg-[#0d64a8] text-white' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'general' && <GeneralSettings />}
      {tab === 'branding' && <BrandingSocials />}
      {tab === 'search' && <SearchChipsEditor />}
      {tab === 'benefits' && <BenefitsEditor />}
      {tab === 'partners' && <PartnersManager />}
      {tab === 'testimonials' && <TestimonialsManager />}
    </div>
  );
};

// ---- General settings ----
const GeneralSettings = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      setForm(res.data);
    } catch (_) {
      setForm({});
    }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      await axios.put(`${API}/admin/site-settings`, form, { headers: adminAuthHeader() });
      setMsg('Saved!');
      setTimeout(() => setMsg(''), 2000);
    } catch (e) {
      setMsg('Failed to save');
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>;

  const groups = [
    {
      title: 'Hero',
      fields: [
        { k: 'hero_subheading', label: 'Hero Subheading (optional)', placeholder: '' },
        { k: 'tagline', label: 'Brand Tagline (near logo)', placeholder: 'journeys, redefined...' },
      ],
    },
    {
      title: 'Section Titles',
      fields: [
        { k: 'section_bestsellers_title', label: 'Bestsellers Section Title' },
        { k: 'section_themes_title', label: 'Themes Section Title' },
        { k: 'section_countries_title', label: 'Trending Countries Title' },
        { k: 'section_states_title', label: 'Trending Indian States Title' },
        { k: 'section_india_title', label: 'India Tour Packages Title' },
        { k: 'section_testimonials_title', label: 'Testimonials Section Title' },
        { k: 'section_partners_title', label: 'Partners Section Title' },
        { k: 'section_blog_title', label: 'Blog Preview Section Title' },
      ],
    },
    {
      title: 'Contact & Footer',
      fields: [
        { k: 'phone', label: 'Phone Number', placeholder: '+91-9650336344' },
        { k: 'email', label: 'Email Address', placeholder: 'info@travelsera.com' },
        { k: 'address', label: 'Office Address', placeholder: '401, Spaze IT Park, Gurgaon...' },
        { k: 'whatsapp', label: 'WhatsApp Number (digits only)', placeholder: '919650336344' },
        { k: 'footer_about', label: 'Footer About Line (optional)', placeholder: 'Making travel easy since 2006' },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {groups.map((g) => (
        <div key={g.title} className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest mb-4">{g.title}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {g.fields.map((f) => (
              <div key={f.k}>
                <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                <input
                  value={form[f.k] || ''}
                  onChange={(e) => upd(f.k, e.target.value)}
                  placeholder={f.placeholder || ''}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]"
                />
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex items-center justify-end gap-3 sticky bottom-4 bg-white border border-gray-200 rounded-lg px-4 py-3 shadow">
        {msg && <span className="text-sm text-green-600">{msg}</span>}
        <button onClick={save} disabled={saving} className="bg-[#0d64a8] hover:bg-[#0a548e] disabled:opacity-70 text-white text-sm px-5 py-2 rounded-md flex items-center gap-1.5">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ---- Branding (logo) & Social media links ----
const BrandingSocials = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/site-settings`).then((r) => setForm(r.data || {})).catch(() => setForm({}));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      await axios.put(`${API}/admin/site-settings`, form, { headers: adminAuthHeader() });
      setMsg('Saved!');
      setTimeout(() => setMsg(''), 2000);
    } catch (_) {
      setMsg('Failed to save');
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>;

  const socials = [
    { k: 'social_facebook', label: 'Facebook URL', placeholder: 'https://facebook.com/yourpage' },
    { k: 'social_instagram', label: 'Instagram URL', placeholder: 'https://instagram.com/yourhandle' },
    { k: 'social_youtube', label: 'YouTube URL', placeholder: 'https://youtube.com/@yourchannel' },
    { k: 'social_twitter', label: 'Twitter / X URL', placeholder: 'https://x.com/yourhandle' },
    { k: 'social_linkedin', label: 'LinkedIn URL', placeholder: 'https://linkedin.com/company/yourpage' },
  ];

  return (
    <div className="space-y-6">
      {/* Hero background image */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest mb-2">Landing Page Hero</h3>
        <p className="text-xs text-gray-500 mb-4">
          The main heading, its highlighted word, and the background image behind the search bar at the top of the home page.
        </p>

        {/* Heading + highlight word + color */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Hero Heading</label>
            <input
              value={form.hero_heading || ''}
              onChange={(e) => upd('hero_heading', e.target.value)}
              placeholder="Explore, Discover, Enjoy"
              data-testid="hero-heading-input"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"
            />
            <p className="text-[11px] text-gray-500 mt-1">Full heading shown at the top of the home page.</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Highlight Word</label>
            <input
              value={form.hero_highlight_word || ''}
              onChange={(e) => upd('hero_highlight_word', e.target.value)}
              placeholder="Discover,"
              data-testid="hero-highlight-input"
              className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]"
            />
            <p className="text-[11px] text-gray-500 mt-1">The word inside the heading that gets colored. Include punctuation (e.g. &ldquo;Discover,&rdquo;).</p>
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs font-medium text-gray-600 mb-2">Highlight Color</label>
            <div className="flex items-center gap-3 flex-wrap">
              {[
                { label: 'Sunny Yellow', hex: '#ffcc29' },
                { label: 'Deep Sky Blue', hex: '#38bdf8' },
                { label: 'Sunset Orange', hex: '#fb923c' },
                { label: 'Coral Pink', hex: '#f472b6' },
                { label: 'Fresh Green', hex: '#34d399' },
                { label: 'White', hex: '#ffffff' },
              ].map((s) => {
                const active = (form.hero_highlight_color || '').toLowerCase() === s.hex.toLowerCase();
                return (
                  <button
                    key={s.hex}
                    type="button"
                    onClick={() => upd('hero_highlight_color', s.hex)}
                    data-testid={`hero-color-${s.hex.slice(1)}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs transition ${active ? 'border-[#0d64a8] bg-[#0d64a8]/5 shadow-sm' : 'border-gray-200 hover:border-gray-300'}`}
                    title={s.label}
                  >
                    <span className="w-4 h-4 rounded-full border border-white shadow-sm" style={{ backgroundColor: s.hex }} />
                    {s.label}
                  </button>
                );
              })}
              <label className="flex items-center gap-2 pl-2 border-l border-gray-200 ml-1">
                <span className="text-xs text-gray-600">Custom:</span>
                <input
                  type="color"
                  value={form.hero_highlight_color || '#ffcc29'}
                  onChange={(e) => upd('hero_highlight_color', e.target.value)}
                  className="w-9 h-9 rounded cursor-pointer border border-gray-200 bg-white"
                  data-testid="hero-color-custom"
                />
                <input
                  type="text"
                  value={form.hero_highlight_color || ''}
                  onChange={(e) => upd('hero_highlight_color', e.target.value)}
                  placeholder="#ffcc29"
                  className="w-24 border border-gray-200 rounded-md px-2 py-1.5 text-xs font-mono focus:border-[#0d64a8]"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Live preview of the heading (on top of a mini hero) */}
        <div className="rounded-md overflow-hidden bg-gradient-to-r from-[#0a2a4a] to-[#0d64a8] px-6 py-6" data-testid="hero-heading-preview">
          <div className="text-[10px] uppercase tracking-widest text-white/70 mb-1">Live preview</div>
          <div className="text-white text-2xl md:text-3xl font-bold drop-shadow">
            {(() => {
              const heading = form.hero_heading || 'Explore, Discover, Enjoy';
              const hw = (form.hero_highlight_word || '').trim();
              const color = form.hero_highlight_color || '#ffcc29';
              if (!hw) return heading;
              const idx = heading.toLowerCase().indexOf(hw.toLowerCase());
              if (idx === -1) return heading;
              const before = heading.slice(0, idx);
              const match = heading.slice(idx, idx + hw.length);
              const after = heading.slice(idx + hw.length);
              return (
                <>
                  {before}
                  <span style={{ color }}>{match}</span>
                  {after}
                </>
              );
            })()}
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">Hero Background Image</h4>
          <p className="text-xs text-gray-500 mb-3">
            Landscape photos with plenty of open sky work best because the heading and search box overlay the left side.
            Recommended: <span className="font-semibold text-[#0d64a8]">1920 × 900 px or larger</span>, JPG or WEBP.
          </p>
          <div className="max-w-xl">
            <ImageUploader
              value={form.hero_image || ''}
              onChange={(url) => upd('hero_image', url)}
              label="Hero Background"
              hint="1920 × 900 px landscape · JPG / WEBP up to 8 MB"
              height="h-40"
            />
          </div>
        </div>
      </div>

      {/* Logo */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest mb-2">Site Logo</h3>
        <p className="text-xs text-gray-500 mb-4">
          Recommended dimensions: <span className="font-semibold text-[#0d64a8]">180 × 50 pixels</span>. Use a PNG with a
          transparent background for best results. The logo will be displayed at this exact fixed size in the site header
          across all pages, so upload a crisp image with those exact proportions to avoid stretching.
        </p>
        <div className="max-w-md">
          <ImageUploader
            value={form.logo_url || ''}
            onChange={(url) => upd('logo_url', url)}
            label="Header Logo (180 × 50 px)"
            hint="PNG / SVG with transparent background · 180 × 50 px"
            height="h-32"
          />
        </div>
        {form.logo_url && (
          <div className="mt-4">
            <div className="text-xs font-medium text-gray-600 mb-2">Preview (actual header size):</div>
            <div className="inline-flex items-center bg-[#0a2a4a] rounded-md p-3">
              <img
                src={form.logo_url}
                alt="Logo preview"
                style={{ height: 50, width: 180, objectFit: 'contain' }}
                data-testid="logo-preview-img"
              />
            </div>
          </div>
        )}
      </div>

      {/* Socials */}
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest mb-2">Social Media Links</h3>
        <p className="text-xs text-gray-500 mb-4">
          Paste the full URL for each social profile. Icons in the footer will only appear for the platforms you fill in.
          Leave a field blank to hide that icon from the footer.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {socials.map((s) => (
            <div key={s.k}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{s.label}</label>
              <input
                type="url"
                value={form[s.k] || ''}
                onChange={(e) => upd(s.k, e.target.value)}
                placeholder={s.placeholder}
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm outline-none focus:border-[#0d64a8]"
                data-testid={`input-${s.k}`}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 sticky bottom-4 bg-white border border-gray-200 rounded-lg px-4 py-3 shadow">
        {msg && <span className="text-sm text-green-600" data-testid="branding-save-msg">{msg}</span>}
        <button onClick={save} disabled={saving} className="bg-[#0d64a8] hover:bg-[#0a548e] disabled:opacity-70 text-white text-sm px-5 py-2 rounded-md flex items-center gap-1.5" data-testid="branding-save-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? 'Saving...' : 'Save Branding & Socials'}
        </button>
      </div>
    </div>
  );
};

// ---- Partners ----
const PartnersManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', logo: '', order: 0 });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/partners`);
      setItems(res.data || []);
    } catch (_) { setItems([]); } finally { setLoading(false); }
  };

  const start = (p) => {
    if (p) {
      setEditingId(p.id);
      setForm({ name: p.name, logo: p.logo || '', order: p.order || 0 });
    } else {
      setEditingId('new');
      setForm({ name: '', logo: '', order: items.length });
    }
  };
  const cancel = () => { setEditingId(null); setForm({ name: '', logo: '', order: 0 }); };

  const save = async () => {
    if (!form.name.trim()) return;
    try {
      if (editingId === 'new') {
        await axios.post(`${API}/admin/partners`, form, { headers: adminAuthHeader() });
      } else {
        await axios.put(`${API}/admin/partners/${editingId}`, form, { headers: adminAuthHeader() });
      }
      await load();
      cancel();
    } catch (_) {}
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this partner?')) return;
    try {
      await axios.delete(`${API}/admin/partners/${id}`, { headers: adminAuthHeader() });
      await load();
    } catch (_) {}
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">These logos appear in the “Our Trusted Hotel Partners” carousel. Upload a logo image for each partner.</p>
        <button onClick={() => start(null)} className="flex items-center gap-1.5 bg-[#0d64a8] text-white text-sm px-4 py-2 rounded-md">
          <Plus size={14} /> Add Partner
        </button>
      </div>

      {editingId !== null && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Partner Name</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder="e.g. Taj Hotels" />
              <label className="block text-xs font-medium text-gray-600 mb-1 mt-3">Order</label>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" />
            </div>
            <div className="md:col-span-2">
              <ImageUploader value={form.logo} onChange={(url) => setForm({ ...form, logo: url })} label="Partner Logo" hint="PNG with transparent background works best" height="h-32" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={cancel} className="text-sm text-gray-600 px-4 py-2 border border-gray-200 rounded-md">Cancel</button>
            <button onClick={save} className="text-sm bg-[#0d64a8] text-white px-4 py-2 rounded-md">Save Partner</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-sm text-gray-500 py-10">Loading...</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {items.map((p) => (
            <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-3 flex flex-col items-center gap-2 hover:shadow-sm">
              <div className="w-full h-20 bg-gray-50 rounded-md flex items-center justify-center overflow-hidden">
                {p.logo ? (
                  <img src={p.logo} alt={p.name} className="max-h-full max-w-full object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <span className="text-sm text-gray-500 font-semibold">{p.name}</span>
                )}
              </div>
              <div className="text-xs font-medium text-gray-800 text-center truncate w-full">{p.name}</div>
              <div className="flex gap-1.5">
                <button onClick={() => start(p)} className="text-[11px] text-[#0d64a8] hover:underline">Edit</button>
                <button onClick={() => remove(p.id)} className="text-[11px] text-red-500 hover:underline">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Testimonials ----
const emptyT = { name: '', role: '', text: '', rating: 5, avatar: '', destination_image: '', order: 0 };
const TestimonialsManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyT);

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/testimonials`);
      setItems(res.data || []);
    } catch (_) { setItems([]); } finally { setLoading(false); }
  };

  const start = (t) => {
    if (t) { setEditingId(t.id); setForm({ ...emptyT, ...t }); }
    else { setEditingId('new'); setForm({ ...emptyT, order: items.length }); }
  };
  const cancel = () => { setEditingId(null); setForm(emptyT); };

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) return;
    try {
      if (editingId === 'new') {
        await axios.post(`${API}/admin/testimonials`, form, { headers: adminAuthHeader() });
      } else {
        await axios.put(`${API}/admin/testimonials/${editingId}`, form, { headers: adminAuthHeader() });
      }
      await load();
      cancel();
    } catch (_) {}
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this testimonial?')) return;
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, { headers: adminAuthHeader() });
      await load();
    } catch (_) {}
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Traveller reviews shown on the home page “What Our Travellers Say” section.</p>
        <button onClick={() => start(null)} className="flex items-center gap-1.5 bg-[#0d64a8] text-white text-sm px-4 py-2 rounded-md">
          <Plus size={14} /> Add Testimonial
        </button>
      </div>

      {editingId !== null && (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder="e.g. Ananya Sharma" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role / Trip</label>
                <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" placeholder="Honeymooner, Bali" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Rating</label>
                  <select value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]">
                    {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Order</label>
                  <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" />
                </div>
              </div>
            </div>
            <div>
              <ImageUploader value={form.avatar} onChange={(url) => setForm({ ...form, avatar: url })} label="Traveller Photo" hint="Square photo works best" height="h-32" />
              <div className="mt-3">
                <ImageUploader value={form.destination_image} onChange={(url) => setForm({ ...form, destination_image: url })} label="Destination Background Image" hint="Shown as top half of the card" height="h-32" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Testimonial Text</label>
              <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} rows={7} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" placeholder="Their quote in their own words..." />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={cancel} className="text-sm text-gray-600 px-4 py-2 border border-gray-200 rounded-md">Cancel</button>
            <button onClick={save} className="text-sm bg-[#0d64a8] text-white px-4 py-2 rounded-md">Save Testimonial</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center text-sm text-gray-500 py-10">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((t) => (
            <div key={t.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-start gap-3">
              <img src={t.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=0d64a8&color=fff`} alt={t.name} className="w-12 h-12 rounded-full object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-gray-900 text-sm">{t.name}</h4>
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} size={11} className="fill-[#ffcc29] text-[#ffcc29]" />)}
                  </div>
                </div>
                <div className="text-[11px] text-gray-500">{t.role}</div>
                <p className="text-xs text-gray-700 mt-1 line-clamp-3">{t.text}</p>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => start(t)} className="text-[11px] text-[#0d64a8] hover:underline">Edit</button>
                <button onClick={() => remove(t.id)} className="text-[11px] text-red-500 hover:underline">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- Search Bar chips ----
const SearchChipsEditor = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/site-settings`).then((r) => setForm(r.data || {})).catch(() => setForm({}));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      // Fetch current settings and merge so unrelated fields aren't lost
      const current = await axios.get(`${API}/site-settings`).then((r) => r.data || {});
      const payload = {
        ...current,
        ...form,
        hero_chips_domestic: (form.hero_chips_domestic_text || '').split(',').map((s) => s.trim()).filter(Boolean),
        hero_chips_international: (form.hero_chips_international_text || '').split(',').map((s) => s.trim()).filter(Boolean),
      };
      delete payload.hero_chips_domestic_text;
      delete payload.hero_chips_international_text;
      await axios.put(`${API}/admin/site-settings`, payload, { headers: adminAuthHeader() });
      setMsg('Saved!');
      setTimeout(() => setMsg(''), 2000);
    } catch (_) {
      setMsg('Failed');
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>;
  const dom = form.hero_chips_domestic_text !== undefined ? form.hero_chips_domestic_text : (form.hero_chips_domestic || []).join(', ');
  const intl = form.hero_chips_international_text !== undefined ? form.hero_chips_international_text : (form.hero_chips_international || []).join(', ');

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest mb-2">Domestic Destinations</h3>
        <p className="text-xs text-gray-500 mb-2">Shown as clickable chips below the hero search bar. Comma-separated. Click a chip on the site to search that destination.</p>
        <textarea
          value={dom}
          onChange={(e) => upd('hero_chips_domestic_text', e.target.value)}
          rows={2}
          placeholder="Darjeeling, Goa, Andaman, Himachal, Kashmir, Kerala, Rajasthan, Sikkim"
          className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"
        />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(dom || '').split(',').map((s) => s.trim()).filter(Boolean).map((n, i) => (
            <span key={i} className="bg-[#e8f2fb] text-[#0d64a8] text-[11px] px-2 py-0.5 rounded-full">{n}</span>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest mb-2">International Destinations</h3>
        <p className="text-xs text-gray-500 mb-2">Comma-separated names shown as chips.</p>
        <textarea
          value={intl}
          onChange={(e) => upd('hero_chips_international_text', e.target.value)}
          rows={2}
          placeholder="Bhutan, Indonesia, Malaysia, Maldives, Singapore, Thailand, Vietnam"
          className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none"
        />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {(intl || '').split(',').map((s) => s.trim()).filter(Boolean).map((n, i) => (
            <span key={i} className="bg-[#fff8e1] text-[#0a2a4a] text-[11px] px-2 py-0.5 rounded-full">{n}</span>
          ))}
        </div>
      </div>
      <div className="flex items-center justify-end gap-3">
        {msg && <span className="text-sm text-green-600">{msg}</span>}
        <button onClick={save} disabled={saving} className="bg-[#0d64a8] hover:bg-[#0a548e] disabled:opacity-70 text-white text-sm px-5 py-2 rounded-md flex items-center gap-1.5">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// ---- Benefits editor ----
const ICON_OPTIONS = ['Award', 'Globe2', 'BadgeIndianRupee', 'ShieldCheck', 'Star', 'Users', 'Trophy', 'Headphones', 'MapPin', 'Sparkles'];

const BenefitsEditor = () => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    axios.get(`${API}/site-settings`).then((r) => setForm(r.data || {})).catch(() => setForm({}));
  }, []);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, form, { headers: adminAuthHeader() });
      setMsg('Saved!');
      setTimeout(() => setMsg(''), 2000);
    } catch (_) {
      setMsg('Failed');
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-sm text-gray-500 py-8 text-center">Loading...</div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">The three benefit cards shown just above the Bestsellers section on the home page.</p>
      {[1, 2, 3].map((n) => (
        <div key={n} className="bg-white border border-gray-200 rounded-lg p-5">
          <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-widest mb-3">Benefit #{n}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Icon</label>
              <select value={form[`benefit_${n}_icon`] || ''} onChange={(e) => upd(`benefit_${n}_icon`, e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:border-[#0d64a8]">
                {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
              <input value={form[`benefit_${n}_title`] || ''} onChange={(e) => upd(`benefit_${n}_title`, e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8]" />
            </div>
            <div className="md:col-span-3">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea value={form[`benefit_${n}_desc`] || ''} onChange={(e) => upd(`benefit_${n}_desc`, e.target.value)} rows={2} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:border-[#0d64a8] resize-none" />
            </div>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-end gap-3 sticky bottom-4 bg-white border border-gray-200 rounded-lg px-4 py-3 shadow">
        {msg && <span className="text-sm text-green-600">{msg}</span>}
        <button onClick={save} disabled={saving} className="bg-[#0d64a8] hover:bg-[#0a548e] disabled:opacity-70 text-white text-sm px-5 py-2 rounded-md flex items-center gap-1.5">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {saving ? 'Saving...' : 'Save Benefits'}
        </button>
      </div>
    </div>
  );
};

export default SiteContentManager;